import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Button } from '../ui';
import { VideoPlayer } from '../components/VideoPlayer';
import { Tabs } from '../components/Tabs';
import { TextInput } from '../components/TextInput';
import { EntriModal } from '../components/EntriModal';
import { GoogleSignInModal } from '../components/GoogleSignInModal';
import { LoadingScreen } from '../components/LoadingScreen';
import { Celebration } from '../components/Celebration';
import { CheckIcon } from '../icons/CheckIcon';

type Step2Tab = 'automatic' | 'manual';

type StepDef = {
  number: number;
  title: string;
  subStepCount: number;
  youtubeId?: string;
  videoUrl?: string;
};

const STEPS: StepDef[] = [
  {
    number: 1,
    title: 'Open Google Postmaster Tools',
    subStepCount: 3,
    videoUrl: '/step1/video.mov',
  },
  { number: 2, title: 'Add the TXT record to your DNS', subStepCount: 2 },
  { number: 3, title: 'Verify your domain in Google Postmaster', subStepCount: 3 },
  { number: 4, title: 'Connect Flodesk with Google', subStepCount: 2 },
  { number: 5, title: 'All set!', subStepCount: 1 },
];

const readStepFromUrl = () => {
  if (typeof window === 'undefined') return 1;
  const stepParam = new URLSearchParams(window.location.search).get('step');
  const step = stepParam ? parseInt(stepParam, 10) : NaN;
  return step >= 1 && step <= STEPS.length ? step : 1;
};

export function StepsScreen({ onComplete }: { onComplete: () => void }) {
  const [activeStep, setActiveStep] = useState(readStepFromUrl);
  const [subStep, setSubStep] = useState(1);
  const [playSignal, setPlaySignal] = useState(0);
  const [step2Tab, setStep2Tab] = useState<Step2Tab>('automatic');
  const [step2Value, setStep2Value] = useState('');
  const [step2Connected, setStep2Connected] = useState(false);
  const [step4Connected, setStep4Connected] = useState(false);
  const [step4Email] = useState('alejandro@flodesk.com');
  const [entriModalOpen, setEntriModalOpen] = useState(false);
  const [googleModalOpen, setGoogleModalOpen] = useState(false);
  const [transitioning, setTransitioning] = useState(false);
  const hasReplacedInitialUrl = useRef(false);

  useEffect(() => {
    const handlePopState = () => {
      const next = readStepFromUrl();
      setActiveStep(next);
      setSubStep(1);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    const url = new URL(window.location.href);
    if (url.searchParams.get('step') === String(activeStep)) {
      hasReplacedInitialUrl.current = true;
      return;
    }
    url.searchParams.set('step', String(activeStep));
    if (!hasReplacedInitialUrl.current) {
      window.history.replaceState(null, '', url);
      hasReplacedInitialUrl.current = true;
    } else {
      window.history.pushState(null, '', url);
    }
  }, [activeStep]);

  const handleSubStepChange = (s: number) => {
    setSubStep(s);
    setPlaySignal(p => p + 1);
  };

  const advance = () => {
    if (activeStep < STEPS.length) {
      setActiveStep(activeStep + 1);
      setSubStep(1);
    } else {
      onComplete();
    }
  };

  const handleStepContinue = () => {
    if (activeStep === 2 && !step2Connected && step2Tab === 'automatic') {
      setEntriModalOpen(true);
    } else if (activeStep === 4 && !step4Connected) {
      setGoogleModalOpen(true);
    } else {
      advance();
    }
  };

  const handleEntriContinue = () => {
    setEntriModalOpen(false);
    setStep2Connected(true);
    advance();
  };

  const handleStep2Reconnect = () => {
    setStep2Connected(false);
    setStep2Value('');
  };

  const handleStep4Reconnect = () => {
    setStep4Connected(false);
  };

  const handleGoogleContinue = () => {
    setGoogleModalOpen(false);
    setTransitioning(true);
    setTimeout(() => {
      setTransitioning(false);
      setStep4Connected(true);
      advance();
    }, 2200);
  };

  const handleStep2TabChange = (tab: Step2Tab) => {
    setStep2Tab(tab);
    setSubStep(1);
  };

  const activeStepDef = STEPS[activeStep - 1];
  const slideCount =
    activeStep === 2 ? (step2Tab === 'manual' ? 3 : 2) : activeStepDef.subStepCount;

  const activeSubStepInfo = (() => {
    if (activeStep === 1) return STEP1_SUBSTEPS[subStep - 1];
    if (activeStep === 2) return STEP2_SUBSTEPS[step2Tab][subStep - 1];
    if (activeStep === 3) return STEP3_SUBSTEPS[subStep - 1];
    if (activeStep === 4) return STEP4_SUBSTEPS[subStep - 1];
    return null;
  })();

  const activeSubSteps = (() => {
    if (activeStep === 1) return STEP1_SUBSTEPS;
    if (activeStep === 2) return STEP2_SUBSTEPS[step2Tab];
    if (activeStep === 3) return STEP3_SUBSTEPS;
    if (activeStep === 4) return STEP4_SUBSTEPS;
    return null;
  })();

  const activeSegments = activeSubSteps?.every(
    s => typeof s.startSec === 'number' && typeof s.endSec === 'number',
  )
    ? activeSubSteps.map(s => ({ startSec: s.startSec!, endSec: s.endSec! }))
    : undefined;

  const activeMediaUrls = activeSubSteps?.some(s => s.mediaUrl)
    ? activeSubSteps.map(s => s.mediaUrl ?? '')
    : undefined;

  return (
    <main className="absolute inset-0 top-[84px] flex items-center justify-center gap-[114px] px-l2">
      <div className="-mt-m flex h-[540px] w-[430px] flex-col gap-[4px] self-center">
        {STEPS.map(step => {
          const offset = step.number - activeStep;
          const isActive = offset === 0;
          const absOffset = Math.abs(offset);
          const renderBody = absOffset <= 1;

          let opacity = 0;
          let scale = 0.94;
          let blur = 4;

          if (isActive) {
            opacity = 1;
            scale = 1;
            blur = 0;
          } else if (absOffset === 1) {
            opacity = 0.35;
            scale = 0.97;
            blur = 1.5;
          } else if (absOffset === 2) {
            opacity = 0.12;
            scale = 0.95;
            blur = 3;
          }

          return (
            <div
              key={step.number}
              className="transition-[opacity,transform,filter] duration-[1100ms] will-change-[opacity,transform,filter]"
              style={{
                opacity,
                transform: `scale(${scale})`,
                filter: `blur(${blur}px)`,
                transformOrigin: 'center',
                transitionTimingFunction: 'cubic-bezier(0.32, 0.72, 0, 1)',
                pointerEvents: isActive ? 'auto' : 'none',
              }}
            >
              <Step
                number={step.number}
                title={step.title}
                status={isActive ? 'active' : 'upcoming'}
              >
                {renderBody && (
                  <div
                    className="overflow-hidden"
                    style={{
                      height: isActive ? 'auto' : '0',
                      paddingBottom: '32px',
                      transition: 'height 1100ms cubic-bezier(0.32, 0.72, 0, 1)',
                      WebkitMaskImage:
                        'linear-gradient(to bottom, black 0, black calc(100% - 40px), transparent 100%)',
                      maskImage:
                        'linear-gradient(to bottom, black 0, black calc(100% - 40px), transparent 100%)',
                    }}
                  >
                    <div
                      className="flex flex-col gap-m"
                      style={{
                        opacity: isActive ? 1 : 0,
                        transition: isActive
                          ? 'opacity 600ms cubic-bezier(0.32, 0.72, 0, 1) 300ms'
                          : 'opacity 240ms ease-out',
                      }}
                    >
                      <StepBody
                        step={step.number}
                        subStep={subStep}
                        onSubStepChange={handleSubStepChange}
                        onContinue={handleStepContinue}
                        step2Tab={step2Tab}
                        onStep2TabChange={handleStep2TabChange}
                        step2Value={step2Value}
                        onStep2ValueChange={setStep2Value}
                        step2Connected={step2Connected}
                        onStep2Reconnect={handleStep2Reconnect}
                        step4Connected={step4Connected}
                        step4Email={step4Email}
                        onStep4Reconnect={handleStep4Reconnect}
                      />
                    </div>
                  </div>
                )}
              </Step>
            </div>
          );
        })}
      </div>

      <div
        key={activeStep === 5 ? 'celebration' : `video-${activeStep}`}
        style={{ animation: 'wizard-fadein 200ms ease-out' }}
      >
        {activeStep === 5 ? (
          <Celebration />
        ) : (
          <VideoPlayer
            title={activeStepDef.title}
            duration="1m"
            slideCount={slideCount}
            currentSlide={subStep}
            onSlideClick={handleSubStepChange}
            playSignal={playSignal}
            subStepTimestamp={activeSubStepInfo?.timestamp}
            subStepTitle={activeSubStepInfo?.title}
            youtubeId={activeStepDef.youtubeId}
            videoUrl={activeStepDef.videoUrl}
            segments={activeSegments}
            mediaUrls={activeStepDef.videoUrl ? undefined : activeMediaUrls}
          />
        )}
      </div>

      <EntriModal
        open={entriModalOpen}
        onClose={() => setEntriModalOpen(false)}
        onContinue={handleEntriContinue}
      />

      <GoogleSignInModal
        open={googleModalOpen}
        onClose={() => setGoogleModalOpen(false)}
        onContinue={handleGoogleContinue}
      />

      {transitioning && <LoadingScreen />}
    </main>
  );
}

function StepBody({
  step,
  subStep,
  onSubStepChange,
  onContinue,
  step2Tab,
  onStep2TabChange,
  step2Value,
  onStep2ValueChange,
  step2Connected,
  onStep2Reconnect,
  step4Connected,
  step4Email,
  onStep4Reconnect,
}: {
  step: number;
  subStep: number;
  onSubStepChange: (s: number) => void;
  onContinue: () => void;
  step2Tab: Step2Tab;
  onStep2TabChange: (tab: Step2Tab) => void;
  step2Value: string;
  onStep2ValueChange: (v: string) => void;
  step2Connected: boolean;
  onStep2Reconnect: () => void;
  step4Connected: boolean;
  step4Email: string;
  onStep4Reconnect: () => void;
}) {
  let body: ReactNode = null;
  if (step === 1)
    body = (
      <Step1Body subStep={subStep} onSubStepChange={onSubStepChange} onContinue={onContinue} />
    );
  else if (step === 2)
    body = (
      <Step2Body
        subStep={subStep}
        onSubStepChange={onSubStepChange}
        onContinue={onContinue}
        tab={step2Tab}
        onTabChange={onStep2TabChange}
        value={step2Value}
        onValueChange={onStep2ValueChange}
        connected={step2Connected}
        onReconnect={onStep2Reconnect}
      />
    );
  else if (step === 3)
    body = (
      <Step3Body subStep={subStep} onSubStepChange={onSubStepChange} onContinue={onContinue} />
    );
  else if (step === 4)
    body = (
      <Step4Body
        subStep={subStep}
        onSubStepChange={onSubStepChange}
        onContinue={onContinue}
        connected={step4Connected}
        email={step4Email}
        onReconnect={onStep4Reconnect}
      />
    );
  else if (step === 5) body = <Step5Body onContinue={onContinue} />;
  if (!body) return null;
  return (
    <div
      className="flex w-full flex-col gap-m"
      style={{ animation: 'wizard-fadein 200ms ease-out' }}
    >
      {body}
    </div>
  );
}

type SubStepDef = {
  startSec?: number;
  endSec?: number;
  mediaUrl?: string;
  timestamp: string;
  title: string;
  description: ReactNode;
};

const STEP1_SUBSTEPS: SubStepDef[] = [
  {
    timestamp: '00:00',
    title: 'Google Postmaster Tools',
    description: (
      <>
        Go to{' '}
        <a
          className="underline"
          href="https://postmaster.google.com"
          target="_blank"
          rel="noreferrer"
          onClick={e => e.stopPropagation()}
        >
          Google Postmaster Tools
        </a>{' '}
        and sign in with your Google account.
      </>
    ),
    startSec: 0,
    endSec: 17,
    mediaUrl: '/step1/1.gif',
  },
  {
    timestamp: '00:17',
    title: 'Add domain',
    description: 'Click Add domain at the top and enter your sending domain.',
    startSec: 17,
    endSec: 37,
    mediaUrl: '/step1/2.gif',
  },
  {
    timestamp: '00:37',
    title: 'TXT record',
    description: "Google generates a TXT record, copy it you'll need it next.",
    startSec: 37,
    endSec: 52,
    mediaUrl: '/step1/3.gif',
  },
];

const STEP3_SUBSTEPS: SubStepDef[] = [
  {
    timestamp: '00:00',
    title: 'Google Postmaster Tools',
    description: 'Head back to Google Postmaster Tools where you started.',
  },
  {
    timestamp: '00:18',
    title: 'Verify and check',
    description: 'Find your domain and click Verify, the check is instant.',
  },
  {
    timestamp: '00:36',
    title: 'Continue the process',
    description: 'Once you see the Verified badge, come back and click Continue.',
  },
];

const STEP4_SUBSTEPS: SubStepDef[] = [
  {
    timestamp: '00:00',
    title: 'Authorize with Google',
    description: 'Click Authorize with Google and pick the account you used for Postmaster Tools.',
  },
  {
    timestamp: '00:42',
    title: 'Permissions',
    description: 'Review the permissions and click Continue, we only read reputation data.',
  },
];

const STEP2_SUBSTEPS: Record<Step2Tab, SubStepDef[]> = {
  automatic: [
    {
      timestamp: '00:00',
      title: 'TXT record',
      description: 'Paste the TXT record you copied from Google Postmaster.',
    },
    {
      timestamp: '00:30',
      title: 'Detect provider',
      description: 'Click Continue, Entri detects your DNS and adds the record.',
    },
  ],
  manual: [
    {
      timestamp: '00:00',
      title: 'Domain provider',
      description: 'Log in to your domain provider (Squarespace, GoDaddy, etc.).',
    },
    {
      timestamp: '00:22',
      title: 'DNS settings',
      description: 'Open your DNS settings and create a new TXT record there.',
    },
    {
      timestamp: '00:44',
      title: 'Save record',
      description: 'Save the record, changes can take a few minutes to propagate.',
    },
  ],
};

function Step4Body({
  subStep,
  onSubStepChange,
  onContinue,
  connected,
  email,
  onReconnect,
}: {
  subStep: number;
  onSubStepChange: (s: number) => void;
  onContinue: () => void;
  connected: boolean;
  email: string;
  onReconnect: () => void;
}) {
  if (connected) {
    return (
      <>
        <div className="ml-[37px] flex items-start justify-between gap-3 rounded-[10px] bg-[#f9f8f8] p-3">
          <div className="flex items-start gap-2">
            <CheckIcon className="mt-[3px] size-3 shrink-0 text-content" />
            <div className="flex flex-col">
              <p className="text-[14px] font-normal leading-[17.8px] text-content">
                Connected to Google
              </p>
              <p className="text-[14px] font-normal leading-[17.8px] text-content-muted">{email}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onReconnect}
            className="shrink-0 text-[14px] font-normal leading-[17.8px] text-content-muted underline transition-colors hover:text-content"
          >
            Reconnect
          </button>
        </div>
        <div className="pl-[61px]">
          <Button onClick={onContinue}>Continue</Button>
        </div>
      </>
    );
  }

  return (
    <>
      <SubSteps>
        {STEP4_SUBSTEPS.map((sub, i) => (
          <SubStep
            key={i}
            timestamp={sub.timestamp}
            title={sub.title}
            description={sub.description}
            active={subStep === i + 1}
            onClick={() => onSubStepChange(i + 1)}
          />
        ))}
      </SubSteps>
      <div className="pl-[61px]">
        <Button onClick={onContinue}>Authorize with Google</Button>
      </div>
    </>
  );
}

function Step5Body({ onContinue }: { onContinue: () => void }) {
  return (
    <>
      <p className="pl-[61px] text-[14px] font-normal leading-[17.8px] text-content-muted">
        Google Postmaster is now connected. Your compliance checklist will appear once Google has
        processed your domain&apos;s data (usually within 24 hours).
      </p>
      <div className="pl-[61px]">
        <Button onClick={onContinue}>Finish</Button>
      </div>
    </>
  );
}

function Step3Body({
  subStep,
  onSubStepChange,
  onContinue,
}: {
  subStep: number;
  onSubStepChange: (s: number) => void;
  onContinue: () => void;
}) {
  return (
    <>
      <SubSteps>
        {STEP3_SUBSTEPS.map((sub, i) => (
          <SubStep
            key={i}
            timestamp={sub.timestamp}
            title={sub.title}
            description={sub.description}
            active={subStep === i + 1}
            onClick={() => onSubStepChange(i + 1)}
          />
        ))}
      </SubSteps>
      <div className="pl-[61px]">
        <Button onClick={onContinue}>Continue</Button>
      </div>
    </>
  );
}

function Step1Body({
  subStep,
  onSubStepChange,
  onContinue,
}: {
  subStep: number;
  onSubStepChange: (s: number) => void;
  onContinue: () => void;
}) {
  return (
    <>
      <SubSteps>
        {STEP1_SUBSTEPS.map((sub, i) => (
          <SubStep
            key={i}
            timestamp={sub.timestamp}
            title={sub.title}
            description={sub.description}
            active={subStep === i + 1}
            onClick={() => onSubStepChange(i + 1)}
          />
        ))}
      </SubSteps>
      <div className="pl-[61px]">
        <Button onClick={onContinue}>Continue</Button>
      </div>
    </>
  );
}

function Step2Body({
  subStep,
  onSubStepChange,
  onContinue,
  tab,
  onTabChange,
  value,
  onValueChange,
  connected,
  onReconnect,
}: {
  subStep: number;
  onSubStepChange: (s: number) => void;
  onContinue: () => void;
  tab: Step2Tab;
  onTabChange: (tab: Step2Tab) => void;
  value: string;
  onValueChange: (v: string) => void;
  connected: boolean;
  onReconnect: () => void;
}) {
  if (connected) {
    return (
      <>
        <div className="ml-[37px] flex items-start justify-between gap-3 rounded-[10px] bg-[#f9f8f8] p-3">
          <div className="flex items-start gap-2">
            <CheckIcon className="mt-[3px] size-3 shrink-0 text-content" />
            <div className="flex flex-col">
              <p className="text-[14px] font-normal leading-[17.8px] text-content">
                Connected via Entri
              </p>
              <p className="text-[14px] font-normal leading-[17.8px] text-content-muted">
                flodesk.com
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onReconnect}
            className="shrink-0 text-[14px] font-normal leading-[17.8px] text-content-muted underline transition-colors hover:text-content"
          >
            Reconnect
          </button>
        </div>
        <div className="pl-[61px]">
          <Button onClick={onContinue}>Continue</Button>
        </div>
      </>
    );
  }

  const subSteps = STEP2_SUBSTEPS[tab];

  return (
    <>
      <div className="pl-[61px]">
        <Tabs
          tabs={[
            { id: 'automatic', label: 'Automatic' },
            { id: 'manual', label: 'Manual' },
          ]}
          active={tab}
          onChange={id => onTabChange(id as Step2Tab)}
        />
      </div>
      <SubSteps>
        {subSteps.map((sub, i) => (
          <SubStep
            key={i}
            timestamp={sub.timestamp}
            title={sub.title}
            description={sub.description}
            active={subStep === i + 1}
            onClick={() => onSubStepChange(i + 1)}
          />
        ))}
      </SubSteps>
      <div className="flex w-full flex-col gap-3 pl-[61px]">
        {tab === 'automatic' && (
          <TextInput
            placeholder="google-site-verification=xxxxxxxxxxxxxxxx"
            value={value}
            onChange={e => onValueChange(e.target.value)}
          />
        )}
        <Button disabled={tab === 'automatic' && value.trim().length === 0} onClick={onContinue}>
          Continue
        </Button>
      </div>
    </>
  );
}

type StepStatus = 'completed' | 'active' | 'upcoming';

function Step({
  number,
  title,
  status,
  onClick,
  children,
}: {
  number: number;
  title: string;
  status: StepStatus;
  onClick?: () => void;
  children?: ReactNode;
}) {
  const titleColor = status === 'active' ? 'text-content' : 'text-content-muted';
  const clickable = !!onClick;
  return (
    <section
      className={`relative flex w-full flex-col rounded-[10px] px-l py-m ${status === 'active' ? 'gap-m' : 'gap-0'}`}
    >
      <button
        type="button"
        onClick={onClick}
        disabled={!clickable}
        className={`group/step -mx-l -my-m flex items-center gap-[21px] rounded-[10px] px-l py-m text-left ${
          clickable ? 'cursor-pointer' : 'cursor-default'
        }`}
      >
        <NumberBadge number={number} status={status} />
        <p
          className={`text-[18px] font-medium leading-[23.2px] transition-colors duration-200 ${titleColor} ${
            clickable ? 'group-hover/step:text-content' : ''
          }`}
        >
          {title}
        </p>
      </button>
      {children}
    </section>
  );
}

function NumberBadge({ number, status }: { number: number; status: StepStatus }) {
  if (status === 'upcoming') {
    return (
      <div
        data-step-badge
        className="relative z-10 flex size-6 shrink-0 items-center justify-center rounded-full bg-[#E7E5E4] text-[14px] font-medium leading-[17.8px] text-content-muted transition-colors duration-200"
      >
        {number}
      </div>
    );
  }

  return (
    <div
      data-step-badge
      className="relative z-10 flex size-6 shrink-0 items-center justify-center rounded-full border border-[rgba(60,50,47,0.12)] bg-content text-[14px] font-medium leading-[17.8px] text-white transition-colors duration-200"
    >
      {status === 'completed' ? <CheckIcon /> : number}
    </div>
  );
}

function SubSteps({ children }: { children: ReactNode }) {
  return <div className="flex w-full flex-col pl-[37px]">{children}</div>;
}

function SubStep({
  timestamp,
  title,
  description,
  active,
  onClick,
}: {
  timestamp: string;
  title: string;
  description?: ReactNode;
  active?: boolean;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`group flex w-full cursor-pointer items-start gap-m text-left transition-colors ${
        active
          ? 'rounded-[16px] bg-[#f9f8f8] px-[24px] py-[20px]'
          : 'rounded-[10px] px-[24px] py-[20px]'
      }`}
    >
      <span className="shrink-0 text-center text-[14px] font-normal leading-[17.8px] text-content">
        {timestamp}
      </span>
      <div className="flex w-[258px] flex-col gap-[6px]">
        <p
          className={`text-[14px] leading-[20px] transition-colors ${
            active
              ? 'font-medium text-content'
              : 'font-normal text-content-muted group-hover:text-content'
          }`}
        >
          {title}
        </p>
        {active && description && (
          <p className="text-[14px] font-normal leading-[17.8px] text-content-muted">
            {description}
          </p>
        )}
      </div>
    </button>
  );
}
